/**
 * The page rail (§10.4).
 *
 * Every page as a thumbnail with its type abbreviated and a quality dot:
 * green (all checks pass), amber (a warning), red (an error), grey (nothing
 * extracted from this page).
 *
 * **Grey is deliberately visible.** A `note_narrative` page with zero
 * narratives is a real finding, and the rail must not hide it by rendering
 * the same as a page that simply had nothing to extract.
 *
 * Two orientations. Vertical is the desktop rail. Horizontal is a filmstrip
 * above the pane, for a viewport that has height to spare and no width --
 * and it stays left-to-right in Arabic for the same reason the panes do not
 * mirror: page 1 is at the left of a document whose own order does not flip.
 *
 * A statement page is marked in both. It is the page people jump to, it was
 * only reachable by pressing 1-4 on a keyboard, and a mark makes it a tap.
 */
import { useEffect, useRef } from 'react'
import type { RailPage } from '@server/sql/documents.ts'
import { Dot, type State } from '~/components/ui/primitives.tsx'
import { useT } from '~/i18n/index.tsx'
import { cn } from '~/lib/cn.ts'
import { useSuperadmin } from '~/lib/superadmin.ts'

/**
 * The rail dot's colour is a *verdict*, so outside the superadmin view it
 * does not have one.
 *
 * `accent` rather than `neutral` for a page that produced something: grey
 * everywhere would say "nothing was extracted here", which is a different
 * claim and a false one. The dot still distinguishes a page with content
 * from an empty one -- it just stops saying whether the content passed.
 */
export function pageState(p: RailPage, superadmin = true): State {
  if (!superadmin) return p.checks.total > 0 ? 'accent' : 'neutral'
  if (p.checks.errors > 0) return 'fail'
  if (p.checks.warnings > 0) return 'warn'
  if (p.checks.total > 0) return 'pass'
  return 'neutral'
}

/** Did this page produce anything at all? */
export const pageIsEmpty = (p: RailPage): boolean =>
  p.extracted.facts + p.extracted.tables + p.extracted.narratives +
  p.extracted.auditors + p.extracted.kam === 0

export function PageRail({
  pages, current, onSelect, arrived, orientation = 'vertical',
}: {
  pages: RailPage[]
  current: number
  onSelect: (pageNo: number) => void
  /** Pages whose image appeared during an on-demand render (§9.4). */
  arrived?: ReadonlySet<number>
  orientation?: 'vertical' | 'horizontal'
}) {
  const { t, v } = useT()
  const superadmin = useSuperadmin()
  const ref = useRef<HTMLUListElement>(null)
  const horizontal = orientation === 'horizontal'

  // Keep the current page in view when it changes from the keyboard, from a
  // swipe or from a jump -- not only from a click. `nearest` on both axes,
  // so this is correct in either orientation.
  useEffect(() => {
    ref.current
      ?.querySelector<HTMLElement>(`[data-page="${current}"]`)
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [current])

  return (
    <nav
      aria-label={t('viewer.rail')}
      dir={horizontal ? 'ltr' : undefined}
      className={cn(
        'shrink-0 bg-[var(--bg-sunken)]',
        horizontal
          ? 'flex w-full flex-col border-b border-[var(--border)]'
          : 'flex h-full w-[86px] flex-col border-e border-[var(--border)]',
      )}
    >
      <ul
        ref={ref}
        className={cn(
          horizontal
            ? 'scroll-x-hint-sunken flex snap-x snap-proximity gap-1 overflow-x-auto overscroll-x-contain px-1 py-1.5'
            : 'min-h-0 flex-1 overflow-y-auto overscroll-contain py-1',
        )}
      >
        {pages.map((p) => {
          const active = p.pageNo === current
          const state = pageState(p, superadmin)
          const empty = pageIsEmpty(p)
          const hasImage = p.hasImage || (arrived?.has(p.pageNo) ?? false)
          const label = v('pageTypeShort', p.pageType) ?? p.pageType ?? '—'
          const isStatement = p.extractor === 'statement'
          const title = `${t('viewer.page', { n: p.pageNo })} · ${v('pageType', p.pageType) ?? p.pageType ?? ''}`
          const thumb = (
            <Thumb
              p={p}
              hasImage={hasImage}
              empty={empty}
              isStatement={isStatement}
              emptyTitle={t('viewer.nothingExtracted')}
            />
          )

          if (horizontal) {
            return (
              <li key={p.id} className="shrink-0 snap-start">
                <button
                  type="button"
                  data-page={p.pageNo}
                  aria-current={active ? 'true' : undefined}
                  onClick={() => onSelect(p.pageNo)}
                  title={title}
                  className={cn(
                    'cv-auto flex w-[54px] flex-col items-center gap-1 rounded-[var(--radius-control)]',
                    'border-b-2 px-1 py-1 transition-colors duration-75',
                    active
                      ? 'border-[var(--accent)] bg-[var(--bg-raised)]'
                      : 'border-transparent hover:bg-[var(--bg-raised)]/70 active:bg-[var(--bg-raised)]',
                  )}
                >
                  {thumb}
                  <span className="flex items-center gap-1">
                    <span
                      className={cn(
                        'tnum text-[length:var(--text-micro)] leading-none',
                        active ? 'font-semibold text-[var(--text)]' : 'text-[var(--text-faint)]',
                      )}
                    >
                      {p.pageNo}
                    </span>
                    <Dot state={state} size={6} />
                  </span>
                </button>
              </li>
            )
          }

          return (
            <li key={p.id}>
              <button
                type="button"
                data-page={p.pageNo}
                aria-current={active ? 'true' : undefined}
                onClick={() => onSelect(p.pageNo)}
                title={title}
                className={cn(
                  'cv-auto group flex w-full gap-1.5 border-s-2 px-1.5 py-1 text-start transition-colors duration-75',
                  active
                    ? 'border-[var(--accent)] bg-[var(--bg-raised)]'
                    : 'border-transparent hover:bg-[var(--bg-raised)]/70 active:bg-[var(--bg-raised)]',
                )}
              >
                <div className="flex w-4 shrink-0 flex-col items-center gap-1 pt-px">
                  <span
                    className={cn(
                      'tnum text-[length:var(--text-micro)] leading-none',
                      active ? 'font-semibold text-[var(--text)]' : 'text-[var(--text-faint)]',
                    )}
                  >
                    {p.pageNo}
                  </span>
                  <Dot state={state} size={6} />
                </div>
                {thumb}
              </button>
              <div
                className={cn(
                  'truncate px-1.5 pb-1 text-[length:var(--text-micro)] leading-[var(--text-micro--line-height)]',
                  active ? 'text-[var(--text)]' : 'text-[var(--text-faint)]',
                )}
                title={v('pageType', p.pageType) ?? p.pageType ?? ''}
              >
                {label}
              </div>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}

/**
 * The Tier A thumbnail, always present. `loading=lazy` plus
 * content-visibility is what makes a 368-page rail cheap -- and it matters
 * more horizontally than vertically, because a filmstrip is one row.
 */
function Thumb({
  p, hasImage, empty, isStatement, emptyTitle,
}: {
  p: RailPage
  hasImage: boolean
  empty: boolean
  isStatement: boolean
  emptyTitle: string
}) {
  return (
    <div className="relative aspect-[1/1.414] w-full overflow-hidden rounded-[2px] border border-[var(--border)] bg-[var(--paper)]">
      {hasImage ? (
        <img
          src={`/media/page/${p.id}?tier=A`}
          alt=""
          loading="lazy"
          decoding="async"
          className="size-full object-cover object-top"
        />
      ) : (
        <div className="size-full bg-[var(--bg-sunken)]" />
      )}
      {/* A primary statement page. Marked because it is the page people jump
          to, and because pressing 1-4 for it is not available on a finger. */}
      {isStatement && (
        <span aria-hidden="true" className="absolute inset-x-0 top-0 h-[3px] bg-[var(--accent)]" />
      )}
      {empty && (
        <span
          className="absolute inset-x-0 bottom-0 bg-[var(--bg-raised)]/85 text-center text-[length:var(--text-micro)] leading-[var(--text-micro--line-height)] text-[var(--text-faint)]"
          title={emptyTitle}
        >
          ·
        </span>
      )}
    </div>
  )
}
